import { useState, useEffect } from "react";
import {
  Bell,
  TrendingUp,
  Mail,
  Eye,
  MousePointer,
  ChevronLeft,
  ChevronRight,
  User,
  Filter,
  Search,
  Calendar,
  Smartphone,
  Monitor,
  Globe,
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import AdminLayout from "@/components/layout/AdminLayout";

import { supabase } from "@/integrations/supabase/client";
import { usePagination } from "@/hooks/usePagination";

interface DeliveryStats {
  total_notifications: number;
  total_pending: number;
  total_sending: number;
  total_delivered: number;
  total_opened: number;
  total_clicked: number;
  total_failed: number;
  delivered_not_opened: number;
  opened_not_clicked: number;
  delivered_and_opened: number;
  opened_and_clicked: number;
  delivery_rate: number;
  open_rate: number;
  click_rate: number;
  failure_rate: number;
}

interface DeliveryRecord {
  delivery_id: number;
  user_id: number;
  template_id: number;
  campaign_id?: number;
  overall_status: string;
  total_devices: number;
  successful_devices: number;
  failed_devices: number;
  opened_at?: string;
  clicked_at?: string;
  opened_device_platform?: string;
  clicked_device_platform?: string;
  created_at: string;
  delivery_success_rate: number;
  was_opened: boolean;
  was_clicked: boolean;
  seconds_to_open?: number;
  seconds_to_click?: number;
  device_breakdown?: any;
  latest_attempt?: any;
  template_name?: string;
  template_category?: string;
  profile_id?: number;
  profile_name?: string;
  profile_email?: string;
  profile_avatar_url?: string;
  // Additional fields for enhanced details
  rendered_title?: string;
  rendered_body?: string;
  action_url?: string;
  image_url?: string;
  template_description?: string;
  template_type?: string;
  template_trigger_event?: string;
  variant_group?: string;
  is_control_group?: boolean;
}

interface UserDevice {
  id: number;
  platform: string;
  device_model?: string;
  app_version?: string;
  os_version?: string;
  is_active: boolean;
  last_used_at: string;
  created_at: string;
  is_target_device?: boolean; // Whether this device received the notification
}

interface DeliveryAttempt {
  id: number;
  delivery_id: number;
  push_token_id: number;
  user_id: number;
  status: "pending" | "sent" | "delivered" | "failed" | "expired";
  attempted_at: string;
  sent_at?: string;
  delivered_at?: string;
  failed_at?: string;
  retry_count?: number;
  next_retry_at?: string;
  error_message?: string;
  error_code?: string;
  fcm_message_id?: string;
  fcm_response?: any;
  platform: string;
  device_model?: string;
  app_version?: string;
  os_version?: string;
  user_timezone?: string;
  metadata?: any;
  created_at: string;
  updated_at: string;
  // Additional fields from user_push_tokens join
  push_token?: string;
  is_active?: boolean;
}

interface PaginatedDeliveries {
  data: DeliveryRecord[];
  total: number;
}

interface Filters {
  userSearch: string;
  templateSearch: string;
  status: string;
  dateFrom: string;
  dateTo: string;
}

const NotificationAnalytics = () => {
  const [stats, setStats] = useState<DeliveryStats>({
    total_notifications: 0,
    total_pending: 0,
    total_sending: 0,
    total_delivered: 0,
    total_opened: 0,
    total_clicked: 0,
    total_failed: 0,
    delivered_not_opened: 0,
    opened_not_clicked: 0,
    delivered_and_opened: 0,
    opened_and_clicked: 0,
    delivery_rate: 0,
    open_rate: 0,
    click_rate: 0,
    failure_rate: 0,
  });
  const [deliveries, setDeliveries] = useState<PaginatedDeliveries>({
    data: [],
    total: 0,
  });
  const [loading, setLoading] = useState(true);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Filter state
  const [filters, setFilters] = useState<Filters>({
    userSearch: "",
    templateSearch: "",
    status: "all",
    dateFrom: "",
    dateTo: "",
  });

  // Dialog state
  const [selectedDelivery, setSelectedDelivery] =
    useState<DeliveryRecord | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [userDevices, setUserDevices] = useState<UserDevice[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [deliveryAttempts, setDeliveryAttempts] = useState<DeliveryAttempt[]>(
    []
  );
  const [loadingAttempts, setLoadingAttempts] = useState(false);

  // Pagination logic
  const { totalPages, canGoPrev, canGoNext, pageNumbers } = usePagination({
    totalItems: deliveries.total,
    itemsPerPage: pageSize,
    currentPage,
  });

  useEffect(() => {
    fetchAnalytics();
  }, []); // Remove filters dependency - analytics should always show 30-day overview

  useEffect(() => {
    fetchDeliveries();
  }, [currentPage, pageSize, filters]);

  const fetchAnalytics = async () => {
    try {
      // Use the new analytics summary view for better performance
      const { data: analyticsData, error: analyticsError } = await supabase
        .from("notification_analytics_summary")
        .select("*")
        .single();

      if (analyticsError) {
        console.error("Error fetching analytics summary:", analyticsError);
        return;
      }

      if (analyticsData) {
        setStats({
          total_notifications: analyticsData.total_notifications || 0,
          total_pending: analyticsData.total_pending || 0,
          total_sending: analyticsData.total_sending || 0,
          total_delivered: analyticsData.total_delivered || 0,
          total_opened: analyticsData.total_opened || 0,
          total_clicked: analyticsData.total_clicked || 0,
          total_failed: analyticsData.total_failed || 0,
          delivered_not_opened: analyticsData.delivered_not_opened || 0,
          opened_not_clicked: analyticsData.opened_not_clicked || 0,
          delivered_and_opened: analyticsData.delivered_and_opened || 0,
          opened_and_clicked: analyticsData.opened_and_clicked || 0,
          delivery_rate: analyticsData.delivery_rate || 0,
          open_rate: analyticsData.open_rate || 0,
          click_rate: analyticsData.click_rate || 0,
          failure_rate: analyticsData.failure_rate || 0,
        });
      }
    } catch (error) {
      console.error("Error fetching notification analytics:", error);
      await fetchAnalyticsManual();
    } finally {
      setLoading(false);
    }
  };

  const fetchDeliveries = async () => {
    try {
      // Build query with filters for count using the stats view
      let countQuery = supabase
        .from("notification_delivery_stats")
        .select("*", { count: "exact", head: true });

      // Apply filters to count query
      if (filters.userSearch) {
        countQuery = countQuery.or(
          `profile_name.ilike.%${filters.userSearch}%,profile_email.ilike.%${filters.userSearch}%`
        );
      }
      if (filters.templateSearch) {
        countQuery = countQuery.or(
          `template_name.ilike.%${filters.templateSearch}%,template_category.ilike.%${filters.templateSearch}%`
        );
      }
      if (filters.status && filters.status !== "all") {
        countQuery = countQuery.eq("overall_status", filters.status);
      }
      if (filters.dateFrom) {
        countQuery = countQuery.gte("created_at", filters.dateFrom);
      }
      if (filters.dateTo) {
        countQuery = countQuery.lte("created_at", filters.dateTo);
      }

      const { count, error: countError } = await countQuery;

      if (countError) throw countError;

      // Fetch paginated deliveries with user information and push token details
      const from = (currentPage - 1) * pageSize;
      const to = from + pageSize - 1;

      let dataQuery = supabase
        .from("notification_delivery_stats")
        .select(
          `
          delivery_id,
          user_id,
          template_id,
          campaign_id,
          overall_status,
          total_devices,
          successful_devices,
          failed_devices,
          opened_at,
          clicked_at,
          opened_device_platform,
          clicked_device_platform,
          created_at,
          delivery_success_rate,
          was_opened,
          was_clicked,
          seconds_to_open,
          seconds_to_click,
          device_breakdown,
          latest_attempt,
          template_name,
          template_category,
          profile_id,
          profile_name,
          profile_email,
          profile_avatar_url,
          rendered_title,
          rendered_body,
          action_url,
          image_url,
          variant_group,
          is_control_group
        `
        )
        .order("created_at", { ascending: false })
        .range(from, to);

      // Apply filters to data query
      if (filters.userSearch) {
        dataQuery = dataQuery.or(
          `profile_name.ilike.%${filters.userSearch}%,profile_email.ilike.%${filters.userSearch}%`
        );
      }
      if (filters.templateSearch) {
        dataQuery = dataQuery.or(
          `template_name.ilike.%${filters.templateSearch}%,template_category.ilike.%${filters.templateSearch}%`
        );
      }
      if (filters.status && filters.status !== "all") {
        dataQuery = dataQuery.eq("overall_status", filters.status);
      }
      if (filters.dateFrom) {
        dataQuery = dataQuery.gte("created_at", filters.dateFrom);
      }
      if (filters.dateTo) {
        dataQuery = dataQuery.lte("created_at", filters.dateTo);
      }

      const { data: paginatedDeliveries, error: deliveriesError } =
        await dataQuery;

      if (deliveriesError) throw deliveriesError;

      setDeliveries({
        data: (paginatedDeliveries as any[]) || [],
        total: count || 0,
      });
    } catch (error) {
      console.error("Error fetching deliveries:", error);
    }
  };

  const handlePageSizeChange = (newPageSize: string) => {
    setPageSize(Number(newPageSize));
    setCurrentPage(1); // Reset to first page
  };

  const handleFilterChange = (key: keyof Filters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  const clearFilters = () => {
    setFilters({
      userSearch: "",
      templateSearch: "",
      status: "all",
      dateFrom: "",
      dateTo: "",
    });
    setCurrentPage(1);
  };

  const handleViewDetails = async (delivery: DeliveryRecord) => {
    setSelectedDelivery(delivery);
    setIsDialogOpen(true);

    // Fetch all user devices
    if (delivery.profile_id) {
      await fetchUserDevices(delivery.profile_id);
    }

    // Fetch delivery attempts
    await fetchDeliveryAttempts(delivery.delivery_id);
  };

  const fetchUserDevices = async (userId: number) => {
    setLoadingDevices(true);
    try {
      const { data: devices, error } = await supabase
        .from("user_push_tokens")
        .select(
          `
          id,
          platform,
          device_model,
          app_version,
          os_version,
          is_active,
          last_used_at,
          created_at
        `
        )
        .eq("user_id", userId)
        .order("last_used_at", { ascending: false });

      if (error) throw error;

      setUserDevices(
        (devices || []).map((device: any) => ({
          ...device,
          is_target_device: false, // No longer relevant in new system
        }))
      );
    } catch (error) {
      console.error("Error fetching user devices:", error);
      setUserDevices([]);
    } finally {
      setLoadingDevices(false);
    }
  };

  const fetchDeliveryAttempts = async (deliveryId: number) => {
    setLoadingAttempts(true);
    try {
      const { data: attempts, error } = await supabase
        .from("notification_delivery_attempts")
        .select(
          `
          *,
          user_push_tokens!inner(
            push_token,
            is_active,
            platform,
            device_model,
            app_version,
            os_version
          )
        `
        )
        .eq("delivery_id", deliveryId)
        .order("attempted_at", { ascending: false });

      if (error) throw error;

      // Transform the data to flatten the joined user_push_tokens
      const transformedAttempts = (attempts as any[]).map((attempt) => ({
        ...attempt,
        push_token: attempt.user_push_tokens?.push_token,
        is_active: attempt.user_push_tokens?.is_active,
        platform: attempt.user_push_tokens?.platform || attempt.platform,
        device_model:
          attempt.user_push_tokens?.device_model || attempt.device_model,
        app_version:
          attempt.user_push_tokens?.app_version || attempt.app_version,
        os_version: attempt.user_push_tokens?.os_version || attempt.os_version,
      }));

      setDeliveryAttempts(transformedAttempts || []);
    } catch (error) {
      console.error("Error fetching delivery attempts:", error);
      setDeliveryAttempts([]);
    } finally {
      setLoadingAttempts(false);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "delivered":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "sending":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      case "opened":
        return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300";
      case "clicked":
        return "bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300";
      case "failed":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
      case "pending":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const getAttemptStatusBadgeColor = (status: string) => {
    switch (status) {
      case "sent":
        return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300";
      case "delivered":
        return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300";
      case "failed":
        return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300";
      case "pending":
        return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300";
      case "expired":
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
      default:
        return "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300";
    }
  };

  const getPlatformIcon = (platform?: string) => {
    if (!platform) return <Globe className="h-4 w-4" />;

    const platformLower = platform.toLowerCase();
    if (
      platformLower.includes("ios") ||
      platformLower.includes("iphone") ||
      platformLower.includes("ipad")
    ) {
      return <Smartphone className="h-4 w-4" />;
    } else if (platformLower.includes("android")) {
      return <Smartphone className="h-4 w-4" />;
    } else if (
      platformLower.includes("web") ||
      platformLower.includes("browser")
    ) {
      return <Monitor className="h-4 w-4" />;
    }
    return <Globe className="h-4 w-4" />;
  };

  const formatDateTime = (dateString?: string) => {
    if (!dateString) return "Not available";
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="flex h-64 w-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <AdminLayout title="Notification Analytics">
      <div className="space-y-6">
        <div>
          <p className="text-muted-foreground">
            Monitor notification delivery performance and engagement metrics for
            the last 30 days
          </p>
        </div>

        {/* Stats Overview */}
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total</CardTitle>
              <Bell className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {stats.total_notifications.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">All notifications</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Successful</CardTitle>
              <Mail className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">
                {(
                  stats.total_delivered +
                  stats.total_opened +
                  stats.total_clicked
                ).toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">
                {stats.delivery_rate}% success rate
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Failed</CardTitle>
              <TrendingUp className="h-4 w-4 text-red-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">
                {stats.total_failed.toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">
                {stats.failure_rate}% failure rate
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Engagement</CardTitle>
              <Eye className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-purple-600">
                {(stats.total_opened + stats.total_clicked).toLocaleString()}
              </div>
              <p className="text-xs text-muted-foreground">
                {stats.open_rate}% engagement rate
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Status Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              Detailed Status Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Pending</span>
                  <Badge
                    variant="outline"
                    className="bg-yellow-50 text-yellow-700 border-yellow-200"
                  >
                    {stats.total_pending}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Sending</span>
                  <Badge
                    variant="outline"
                    className="bg-blue-50 text-blue-700 border-blue-200"
                  >
                    {stats.total_sending}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Delivered</span>
                  <Badge
                    variant="outline"
                    className="bg-green-50 text-green-700 border-green-200"
                  >
                    {stats.total_delivered}
                  </Badge>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Opened</span>
                  <Badge
                    variant="outline"
                    className="bg-purple-50 text-purple-700 border-purple-200"
                  >
                    {stats.total_opened}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Clicked</span>
                  <Badge
                    variant="outline"
                    className="bg-indigo-50 text-indigo-700 border-indigo-200"
                  >
                    {stats.total_clicked}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Failed</span>
                  <Badge
                    variant="outline"
                    className="bg-red-50 text-red-700 border-red-200"
                  >
                    {stats.total_failed}
                  </Badge>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    Delivered (Not Opened)
                  </span>
                  <Badge
                    variant="outline"
                    className="bg-orange-50 text-orange-700 border-orange-200"
                  >
                    {stats.delivered_not_opened}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    Delivered & Opened
                  </span>
                  <Badge
                    variant="outline"
                    className="bg-teal-50 text-teal-700 border-teal-200"
                  >
                    {stats.delivered_and_opened}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">
                    Opened (Not Clicked)
                  </span>
                  <Badge
                    variant="outline"
                    className="bg-amber-50 text-amber-700 border-amber-200"
                  >
                    {stats.opened_not_clicked}
                  </Badge>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Opened & Clicked</span>
                  <Badge
                    variant="outline"
                    className="bg-emerald-50 text-emerald-700 border-emerald-200"
                  >
                    {stats.opened_and_clicked}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Click Rate</span>
                  <Badge
                    variant="outline"
                    className="bg-cyan-50 text-cyan-700 border-cyan-200"
                  >
                    {stats.click_rate}%
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Open Rate</span>
                  <Badge
                    variant="outline"
                    className="bg-violet-50 text-violet-700 border-violet-200"
                  >
                    {stats.open_rate}%
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Filters for Delivery Listing */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filter Deliveries
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
              <div className="space-y-2">
                <label className="text-sm font-medium">User Search</label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by name or email..."
                    value={filters.userSearch}
                    onChange={(e) =>
                      handleFilterChange("userSearch", e.target.value)
                    }
                    className="pl-8"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Template Search</label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search by template name or category..."
                    value={filters.templateSearch}
                    onChange={(e) =>
                      handleFilterChange("templateSearch", e.target.value)
                    }
                    className="pl-8"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Status</label>
                <Select
                  value={filters.status}
                  onValueChange={(value) => handleFilterChange("status", value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    <SelectItem value="pending">Pending</SelectItem>
                    <SelectItem value="sending">Sending</SelectItem>
                    <SelectItem value="delivered">Delivered</SelectItem>
                    <SelectItem value="opened">Opened</SelectItem>
                    <SelectItem value="clicked">Clicked</SelectItem>
                    <SelectItem value="failed">Failed</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">From Date</label>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) =>
                    handleFilterChange("dateFrom", e.target.value)
                  }
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">To Date</label>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => handleFilterChange("dateTo", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">&nbsp;</label>
                <Button
                  variant="outline"
                  onClick={clearFilters}
                  className="w-full"
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent Deliveries */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Recent Deliveries
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Template</TableHead>
                  <TableHead>Delivery Info</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created / Devices</TableHead>
                  <TableHead>Opened</TableHead>
                  <TableHead>Clicked</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {deliveries.data.map((delivery) => (
                  <TableRow key={delivery.delivery_id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="h-8 w-8">
                          <AvatarImage
                            src={delivery.profile_avatar_url || ""}
                            alt={delivery.profile_name || "User"}
                          />
                          <AvatarFallback>
                            <User className="h-4 w-4" />
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <div className="font-medium">
                            {delivery.profile_name || "Unknown User"}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {delivery.profile_email || "No email"}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">
                          {delivery.template_name}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {delivery.template_category}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <div className="flex items-center gap-2">
                        <span className="truncate">
                          Delivery #{delivery.delivery_id}
                        </span>
                        <div className="flex items-center gap-1 text-xs text-muted-foreground">
                          <span>
                            {delivery.successful_devices}/
                            {delivery.total_devices}
                          </span>
                          <span>devices</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <Badge
                          className={getStatusBadgeColor(
                            delivery.overall_status
                          )}
                        >
                          {delivery.overall_status}
                        </Badge>
                        <div className="text-xs text-muted-foreground">
                          {delivery.delivery_success_rate.toFixed(1)}% success
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      <div className="flex flex-col gap-1">
                        <span>
                          {new Date(delivery.created_at).toLocaleString()}
                        </span>
                        {delivery.successful_devices > 0 && (
                          <span className="text-xs text-green-600">
                            {delivery.successful_devices} sent
                          </span>
                        )}
                        {delivery.failed_devices > 0 && (
                          <span className="text-xs text-red-600">
                            {delivery.failed_devices} failed
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">
                      {delivery.was_opened ? (
                        <div className="flex flex-col">
                          <span>
                            {new Date(delivery.opened_at!).toLocaleString()}
                          </span>
                          {delivery.opened_device_platform && (
                            <span className="text-xs text-muted-foreground">
                              via {delivery.opened_device_platform}
                            </span>
                          )}
                        </div>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    {/* <TableCell className="text-sm">
                      {delivery.opened_at
                        ? new Date(delivery.opened_at).toLocaleString()
                        : "-"}
                    </TableCell> */}
                    <TableCell className="text-sm">
                      {delivery.was_clicked ? (
                        <div className="flex flex-col">
                          <span>
                            {new Date(delivery.clicked_at!).toLocaleString()}
                          </span>
                          {delivery.clicked_device_platform && (
                            <span className="text-xs text-muted-foreground">
                              via {delivery.clicked_device_platform}
                            </span>
                          )}
                        </div>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleViewDetails(delivery)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {deliveries.data.length === 0 && (
              <div className="text-center py-8">
                <Bell className="mx-auto h-12 w-12 text-muted-foreground" />
                <h3 className="mt-2 text-sm font-semibold">
                  No deliveries found
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {Object.values(filters).some((f) => f)
                    ? "Try adjusting your filters to see more results"
                    : "Start sending notifications to see analytics data"}
                </p>
              </div>
            )}
          </CardContent>

          {/* Pagination Controls */}
          {deliveries.total > 0 && (
            <div className="flex items-center justify-between px-4 py-4 border-t border-border">
              <div className="text-sm text-muted-foreground">
                Showing{" "}
                <span className="font-medium">
                  {Math.min((currentPage - 1) * pageSize + 1, deliveries.total)}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(currentPage * pageSize, deliveries.total)}
                </span>{" "}
                of <span className="font-medium">{deliveries.total}</span>{" "}
                deliveries
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-center">
                  <Button
                    className="mr-1 px-2"
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(1)}
                    disabled={!canGoPrev}
                  >
                    First
                  </Button>

                  <Button
                    className="mr-1 px-2"
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={!canGoPrev}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>

                  {pageNumbers.map((pageNum) => (
                    <Button
                      key={pageNum}
                      className="mx-0.5 w-8 h-8 p-0"
                      variant={pageNum === currentPage ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  ))}

                  <Button
                    className="ml-1 px-2"
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={!canGoNext}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>

                  <Button
                    className="ml-1 px-2"
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(totalPages)}
                    disabled={!canGoNext}
                  >
                    Last
                  </Button>
                </div>

                <Select
                  value={String(pageSize)}
                  onValueChange={handlePageSizeChange}
                >
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="20">20 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                    <SelectItem value="100">100 per page</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Enhanced Delivery Details Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-6xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Notification Delivery Details
            </DialogTitle>
          </DialogHeader>
          {selectedDelivery && (
            <ScrollArea className="max-h-[80vh]">
              <div className="space-y-6">
                {/* Header with Key Information */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  {/* User Information */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      User Information
                    </h4>
                    <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                      <Avatar className="h-10 w-10">
                        <AvatarImage
                          src={selectedDelivery.profile_avatar_url || ""}
                          alt={selectedDelivery.profile_name || "User"}
                        />
                        <AvatarFallback>
                          <User className="h-5 w-5" />
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <div className="font-medium">
                          {selectedDelivery.profile_name || "Unknown User"}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {selectedDelivery.profile_email || "No email"}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          User ID: {selectedDelivery.user_id}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Template Information */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      Template Information
                    </h4>
                    <div className="p-3 bg-muted/30 rounded-lg space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Template:</span>
                        <Badge variant="outline" className="text-xs">
                          {selectedDelivery.template_name || "Unknown"}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Category:</span>
                        <Badge variant="secondary" className="text-xs">
                          {selectedDelivery.template_category || "Unknown"}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">
                          Template ID:
                        </span>
                        <span className="text-xs font-mono">
                          {selectedDelivery.template_id}
                        </span>
                      </div>
                      {selectedDelivery.variant_group && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Variant:</span>
                          <Badge variant="outline" className="text-xs">
                            {selectedDelivery.variant_group}
                          </Badge>
                        </div>
                      )}
                      {selectedDelivery.is_control_group && (
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium">Group:</span>
                          <Badge variant="destructive" className="text-xs">
                            Control Group
                          </Badge>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Delivery Status */}
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      Delivery Status
                    </h4>
                    <div className="p-3 bg-muted/30 rounded-lg space-y-2">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Status:</span>
                        <Badge
                          className={getStatusBadgeColor(
                            selectedDelivery.overall_status
                          )}
                        >
                          {selectedDelivery.overall_status}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">
                          Success Rate:
                        </span>
                        <span className="text-sm font-mono">
                          {selectedDelivery.delivery_success_rate.toFixed(1)}%
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">Devices:</span>
                        <span className="text-sm">
                          {selectedDelivery.successful_devices}/
                          {selectedDelivery.total_devices}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">
                          Delivery ID:
                        </span>
                        <span className="text-xs font-mono">
                          {selectedDelivery.delivery_id}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <Separator />

                {/* Notification Content Preview */}
                {selectedDelivery.rendered_title && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      Notification Content
                    </h4>
                    <div className="p-4 bg-muted/30 rounded-lg space-y-3">
                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                        <span className="font-medium">Title</span>
                      </div>
                      <div className="text-sm bg-background p-3 rounded border">
                        {selectedDelivery.rendered_title}
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="font-medium">Body</span>
                      </div>
                      <div className="text-sm bg-background p-3 rounded border">
                        {selectedDelivery.rendered_body}
                      </div>

                      {selectedDelivery.action_url && (
                        <>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                            <span className="font-medium">Action URL</span>
                          </div>
                          <div className="text-sm bg-background p-3 rounded border font-mono">
                            {selectedDelivery.action_url}
                          </div>
                        </>
                      )}

                      {selectedDelivery.image_url && (
                        <>
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                            <span className="font-medium">Image</span>
                          </div>
                          <div className="text-sm bg-background p-3 rounded border">
                            <img
                              src={selectedDelivery.image_url}
                              alt="Notification"
                              className="max-w-full h-auto rounded"
                            />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                )}

                <Separator />

                {/* Delivery Timeline */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                    Delivery Timeline
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <div className="text-sm font-medium text-muted-foreground">
                        Created
                      </div>
                      <div className="text-sm font-mono">
                        {formatDateTime(selectedDelivery.created_at)}
                      </div>
                    </div>

                    {selectedDelivery.opened_at && (
                      <div className="p-3 bg-muted/30 rounded-lg">
                        <div className="text-sm font-medium text-muted-foreground">
                          Opened
                        </div>
                        <div className="text-sm font-mono">
                          {formatDateTime(selectedDelivery.opened_at)}
                        </div>
                        {selectedDelivery.opened_device_platform && (
                          <div className="text-xs text-muted-foreground mt-1">
                            via {selectedDelivery.opened_device_platform}
                          </div>
                        )}
                      </div>
                    )}

                    {selectedDelivery.clicked_at && (
                      <div className="p-3 bg-muted/30 rounded-lg">
                        <div className="text-sm font-medium text-muted-foreground">
                          Clicked
                        </div>
                        <div className="text-sm font-mono">
                          {formatDateTime(selectedDelivery.clicked_at)}
                        </div>
                        {selectedDelivery.clicked_device_platform && (
                          <div className="text-xs text-muted-foreground mt-1">
                            via {selectedDelivery.clicked_device_platform}
                          </div>
                        )}
                      </div>
                    )}

                    {selectedDelivery.seconds_to_open && (
                      <div className="p-3 bg-muted/30 rounded-lg">
                        <div className="text-sm font-medium text-muted-foreground">
                          Time to Open
                        </div>
                        <div className="text-sm font-mono">
                          {Math.round(selectedDelivery.seconds_to_open)} seconds
                        </div>
                      </div>
                    )}

                    {selectedDelivery.seconds_to_click && (
                      <div className="p-3 bg-muted/30 rounded-lg">
                        <div className="text-sm font-medium text-muted-foreground">
                          Time to Click
                        </div>
                        <div className="text-sm font-mono">
                          {Math.round(selectedDelivery.seconds_to_click)}{" "}
                          seconds
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Device Breakdown */}
                {selectedDelivery.device_breakdown && (
                  <div className="space-y-3">
                    <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      Device Platform Breakdown
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {Object.entries(selectedDelivery.device_breakdown).map(
                        ([platform, stats]) => {
                          const deviceStats = stats as
                            | {
                                total?: number;
                                success?: number;
                                failed?: number;
                                pending?: number;
                              }
                            | number;
                          const totalCount =
                            typeof deviceStats === "object"
                              ? deviceStats.total ||
                                deviceStats.success +
                                  deviceStats.failed +
                                  deviceStats.pending ||
                                0
                              : deviceStats;

                          return (
                            <div
                              key={platform}
                              className="p-3 bg-muted/30 rounded-lg"
                            >
                              <div className="flex items-center gap-2 mb-2">
                                {getPlatformIcon(platform)}
                                <span className="font-medium capitalize">
                                  {platform}
                                </span>
                                <Badge variant="outline">{totalCount}</Badge>
                              </div>
                              {typeof deviceStats === "object" && (
                                <div className="space-y-1 text-xs">
                                  {deviceStats.success !== undefined && (
                                    <div className="flex justify-between">
                                      <span className="text-green-600">
                                        Success:
                                      </span>
                                      <span>{deviceStats.success}</span>
                                    </div>
                                  )}
                                  {deviceStats.failed !== undefined && (
                                    <div className="flex justify-between">
                                      <span className="text-red-600">
                                        Failed:
                                      </span>
                                      <span>{deviceStats.failed}</span>
                                    </div>
                                  )}
                                  {deviceStats.pending !== undefined && (
                                    <div className="flex justify-between">
                                      <span className="text-yellow-600">
                                        Pending:
                                      </span>
                                      <span>{deviceStats.pending}</span>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        }
                      )}
                    </div>
                  </div>
                )}

                <Separator />

                {/* User Devices */}
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                      User Devices ({userDevices.length})
                    </h4>
                    {loadingDevices && (
                      <span className="text-sm text-muted-foreground">
                        Loading...
                      </span>
                    )}
                  </div>

                  {userDevices.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {userDevices.map((device) => (
                        <div
                          key={device.id}
                          className={`p-3 border rounded-lg ${
                            device.is_target_device
                              ? "border-blue-300/50 bg-blue-500/10"
                              : "border-border bg-muted/30"
                          }`}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {getPlatformIcon(device.platform)}
                              <span className="font-medium capitalize">
                                {device.platform}
                              </span>
                              {device.is_target_device && (
                                <Badge variant="secondary" className="text-xs">
                                  Target Device
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              <div
                                className={`w-2 h-2 rounded-full ${
                                  device.is_active
                                    ? "bg-green-500"
                                    : "bg-red-500"
                                }`}
                              ></div>
                              <span className="text-xs text-muted-foreground">
                                {device.is_active ? "Active" : "Inactive"}
                              </span>
                            </div>
                          </div>

                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <span className="text-muted-foreground">
                                Device:
                              </span>
                              <span className="ml-1">
                                {device.device_model || "Unknown"}
                              </span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">
                                App:
                              </span>
                              <span className="ml-1">
                                {device.app_version || "Unknown"}
                              </span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">OS:</span>
                              <span className="ml-1">
                                {device.os_version || "Unknown"}
                              </span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">
                                Last Used:
                              </span>
                              <span className="ml-1">
                                {formatDateTime(device.last_used_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : !loadingDevices ? (
                    <div className="text-sm text-muted-foreground p-3 border border-dashed rounded-lg">
                      No devices found for this user
                    </div>
                  ) : null}
                </div>

                <Separator />

                {/* Delivery Attempts */}
                <div className="space-y-3">
                  <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                    Delivery Attempts ({deliveryAttempts.length})
                  </h4>

                  {/* Platform-wise Summary */}
                  {deliveryAttempts.length > 0 && (
                    <div className="p-3 bg-muted/30 rounded-lg">
                      <h5 className="text-sm font-medium mb-2">
                        Platform-wise Delivery Summary
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {(() => {
                          const platformStats = deliveryAttempts.reduce(
                            (acc, attempt) => {
                              const platform = attempt.platform || "Unknown";
                              if (!acc[platform]) {
                                acc[platform] = {
                                  total: 0,
                                  delivered: 0,
                                  failed: 0,
                                  pending: 0,
                                  devices: new Set(),
                                };
                              }
                              acc[platform].total++;
                              acc[platform].devices.add(
                                attempt.device_model || "Unknown"
                              );

                              if (attempt.status === "delivered")
                                acc[platform].delivered++;
                              else if (attempt.status === "failed")
                                acc[platform].failed++;
                              else if (attempt.status === "pending")
                                acc[platform].pending++;

                              return acc;
                            },
                            {} as Record<
                              string,
                              {
                                total: number;
                                delivered: number;
                                failed: number;
                                pending: number;
                                devices: Set<string>;
                              }
                            >
                          );

                          return Object.entries(platformStats).map(
                            ([platform, stats]) => (
                              <div
                                key={platform}
                                className="p-2 bg-background rounded border"
                              >
                                <div className="flex items-center gap-2 mb-1">
                                  {getPlatformIcon(platform)}
                                  <span className="text-sm font-medium capitalize">
                                    {platform}
                                  </span>
                                </div>
                                <div className="text-xs space-y-1">
                                  <div className="flex justify-between">
                                    <span>Total:</span>
                                    <span className="font-medium">
                                      {stats.total}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-green-600">
                                      Delivered:
                                    </span>
                                    <span className="font-medium text-green-600">
                                      {stats.delivered}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-red-600">
                                      Failed:
                                    </span>
                                    <span className="font-medium text-red-600">
                                      {stats.failed}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-yellow-600">
                                      Pending:
                                    </span>
                                    <span className="font-medium text-yellow-600">
                                      {stats.pending}
                                    </span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span>Devices:</span>
                                    <span className="font-medium">
                                      {stats.devices.size}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            )
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  <div className="space-y-3">
                    {loadingAttempts ? (
                      <div className="text-center py-4">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                      </div>
                    ) : deliveryAttempts.length === 0 ? (
                      <div className="text-sm text-muted-foreground p-3 border border-dashed rounded-lg">
                        No delivery attempts found for this notification.
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {deliveryAttempts.map((attempt) => (
                          <div
                            key={attempt.id}
                            className="p-4 border rounded-lg bg-muted/30"
                          >
                            {/* Attempt Header */}
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <Badge
                                  className={getAttemptStatusBadgeColor(
                                    attempt.status
                                  )}
                                >
                                  {attempt.status}
                                </Badge>
                                <div className="flex items-center gap-2">
                                  {getPlatformIcon(attempt.platform)}
                                  <span className="text-sm font-medium capitalize">
                                    {attempt.platform}
                                  </span>
                                  <Badge variant="outline" className="text-xs">
                                    {attempt.device_model || "Unknown Device"}
                                  </Badge>
                                </div>
                                {attempt.retry_count &&
                                  attempt.retry_count > 0 && (
                                    <Badge
                                      variant="outline"
                                      className="text-xs"
                                    >
                                      Retry #{attempt.retry_count}
                                    </Badge>
                                  )}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {formatDateTime(attempt.attempted_at)}
                              </div>
                            </div>

                            {/* Device Information */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 text-xs">
                              <div>
                                <span className="text-muted-foreground">
                                  Device:
                                </span>
                                <div className="font-medium">
                                  {attempt.device_model || "Unknown"}
                                </div>
                              </div>
                              <div>
                                <span className="text-muted-foreground">
                                  App Version:
                                </span>
                                <div className="font-medium">
                                  {attempt.app_version || "Unknown"}
                                </div>
                              </div>
                              <div>
                                <span className="text-muted-foreground">
                                  OS Version:
                                </span>
                                <div className="font-medium">
                                  {attempt.os_version || "Unknown"}
                                </div>
                              </div>
                              <div>
                                <span className="text-muted-foreground">
                                  Token Status:
                                </span>
                                <div className="font-medium">
                                  <span
                                    className={
                                      attempt.is_active
                                        ? "text-green-600"
                                        : "text-red-600"
                                    }
                                  >
                                    {attempt.is_active ? "Active" : "Inactive"}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {/* Timeline */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3 text-xs">
                              {attempt.sent_at && (
                                <div>
                                  <span className="text-muted-foreground">
                                    Sent:
                                  </span>
                                  <div className="font-medium">
                                    {formatDateTime(attempt.sent_at)}
                                  </div>
                                </div>
                              )}
                              {attempt.delivered_at && (
                                <div className="bg-green-50 dark:bg-green-900/20 p-2 rounded">
                                  <span className="text-green-700 dark:text-green-300 font-medium">
                                    ✓ Delivered:
                                  </span>
                                  <div className="font-medium text-green-800 dark:text-green-200">
                                    {formatDateTime(attempt.delivered_at)}
                                  </div>
                                </div>
                              )}
                              {attempt.failed_at && (
                                <div>
                                  <span className="text-muted-foreground">
                                    Failed:
                                  </span>
                                  <div className="font-medium">
                                    {formatDateTime(attempt.failed_at)}
                                  </div>
                                </div>
                              )}
                              {attempt.next_retry_at && (
                                <div>
                                  <span className="text-muted-foreground">
                                    Next Retry:
                                  </span>
                                  <div className="font-medium">
                                    {formatDateTime(attempt.next_retry_at)}
                                  </div>
                                </div>
                              )}
                            </div>

                            {/* Error Information */}
                            {attempt.error_message && (
                              <div className="mb-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-md">
                                <div className="flex items-center gap-2 mb-1">
                                  <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                                  <span className="text-sm font-medium text-red-700 dark:text-red-300">
                                    Error Details
                                  </span>
                                </div>
                                <div className="text-sm text-red-600 dark:text-red-400">
                                  {attempt.error_message}
                                </div>
                                {attempt.error_code && (
                                  <div className="text-xs text-red-500 dark:text-red-400 mt-1">
                                    Code: {attempt.error_code}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* FCM Information */}
                            {(attempt.fcm_message_id ||
                              attempt.fcm_response) && (
                              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-md">
                                <div className="flex items-center gap-2 mb-2">
                                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                                  <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                                    FCM Information
                                  </span>
                                </div>
                                {attempt.fcm_message_id && (
                                  <div className="text-sm mb-2">
                                    <span className="text-muted-foreground">
                                      Message ID:
                                    </span>
                                    <div className="font-mono text-xs">
                                      {attempt.fcm_message_id}
                                    </div>
                                  </div>
                                )}
                                {attempt.fcm_response && (
                                  <div className="text-sm">
                                    <span className="text-muted-foreground">
                                      Response:
                                    </span>
                                    <pre className="whitespace-pre-wrap text-xs mt-1 bg-background p-2 rounded border">
                                      {JSON.stringify(
                                        attempt.fcm_response,
                                        null,
                                        2
                                      )}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Push Token (truncated for security) */}
                            {attempt.push_token && (
                              <div className="text-xs text-muted-foreground">
                                <span>Token:</span>
                                <div className="font-mono">
                                  {attempt.push_token.length > 50
                                    ? `${attempt.push_token.substring(
                                        0,
                                        50
                                      )}...`
                                    : attempt.push_token}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default NotificationAnalytics;
